import React, { useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchGamesByCategory } from '../redux/actions/gameActions';
import GameCard from './GameCard';
import '../styles/GameGrid.css';

const GameGrid = () => {
  const dispatch = useDispatch();
  const { games, loading, error } = useSelector(state => state.games);
  const { selectedCategory, categories } = useSelector(state => state.categories);
  const prevCategoryRef = useRef(selectedCategory);
  
  // 使用 useRef 来保存之前的游戏列表
  const prevGamesListRef = useRef([]);

  // 监听分类变化并重新加载数据
  useEffect(() => {
    if (selectedCategory !== prevCategoryRef.current) {
      console.log('Category changed, reloading games:', {
        prevCategory: prevCategoryRef.current,
        newCategory: selectedCategory
      });
      
      // 更新前一个分类的引用
      prevCategoryRef.current = selectedCategory;
      
      // 清空之前的游戏列表
      prevGamesListRef.current = [];
      
      // 加载新分类的游戏
      dispatch(fetchGamesByCategory(selectedCategory));
    }
  }, [selectedCategory, dispatch]);

  // 使用 useMemo 缓存 gamesList
  const gamesList = useMemo(() => {
    const newGamesList = Array.isArray(games) ? games : [];
    
    // 如果新数据为空且之前有数据，且分类没有改变，保留现有数据
    if (newGamesList.length === 0 && 
        prevGamesListRef.current.length > 0 && 
        selectedCategory === prevCategoryRef.current) {
      return prevGamesListRef.current;
    }

    // 更新引用并返回新数据
    prevGamesListRef.current = newGamesList;
    return newGamesList;
  }, [games, selectedCategory]);

  // 获取当前选中分类的名称
  const getSelectedCategoryName = () => {
    if (!selectedCategory || !Array.isArray(categories)) return 'All Games';
    const category = categories.find(cat => cat.slug === selectedCategory);
    return category ? category.name : 'All Games';
  };

  // 只在开发环境下记录渲染信息
  if (process.env.NODE_ENV === 'development') {
    console.log('GameGrid render:', {
      gamesCount: gamesList.length,
      selectedCategory,
      selectedCategoryName: getSelectedCategoryName(),
      renderTime: new Date().toISOString(),
      hasExistingData: gamesList.length > 0
    });
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div>Loading games...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-state">
        <div>Error: {error}</div>
      </div>
    );
  }

  // 只在没有任何数据时显示空状态
  if (!gamesList || (gamesList.length === 0 && !loading)) {
    return (
      <div className="empty-state">
        <p>
          {getSelectedCategoryName() === 'All Games'
            ? 'No games available'
            : `No games found in ${getSelectedCategoryName()}`}
        </p>
      </div>
    );
  }

  return (
    <div className="game-grid-container">
      <h2 className="category-title">
        <span className="category-name">{getSelectedCategoryName()}</span>
        <span className="category-count">({gamesList.length} Games)</span>
      </h2>
      <div className="game-grid">
        {gamesList.map(game => (
          <GameCard
            key={game._id || game.id}
            id={game._id || game.id}
            title={game.title || 'Untitled Game'}
            image={game.image || game.thumbnail}
            category={game.categoryName || 'Uncategorized'}
            gameUrl={game.gameUrl}
          />
        ))}
      </div>
    </div>
  );
};

// 使用 React.memo 来避免不必要的重渲染
export default React.memo(GameGrid);
